import { useEffect } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';

/**
 * Shared LaunchDarkly + copy definitions for the Request Manager PLG (Product-Led Growth)
 * surfaces: the fixed sidebar card (ProviderExchangeCard, PP-4934) and the dismissible popup
 * modal (ProviderExchangePopupModal, PP-4935).
 *
 * A single boolean-gate/multivariate flag (`rm-plg`) gates both surfaces and determines which
 * copy variation a user sees. See the RM PLG Technical Spec for the targeting rules.
 */

/** LaunchDarkly gate flag name. `useFlags()` camelCases it, so it is read as `flags.rmPlgSideNavModal`. */
export const RM_PLG_FLAG = 'rmPlgSideNavModal';

/**
 * LaunchDarkly boolean ("RM PLG Side Nav No Modal") that pauses only the popup modal. `useFlags()`
 * camelCases `rm-plg-side-nav-no-modal`. Paused only when explicitly `true`, so a missing flag keeps
 * the popup on. While paused, the sidebar card shows without waiting for the popup to be dismissed.
 */
export const RM_PLG_NO_MODAL_FLAG = 'rmPlgSideNavNoModal';

/** True when the popup is paused via LaunchDarkly. */
export const useRmPlgPopupPaused = (): boolean => {
    const flags = useFlags();
    return flags[RM_PLG_NO_MODAL_FLAG] === true;
};

/**
 * Variation values returned by the `rm-plg` flag. The `off` variation (or any unrecognized value)
 * shows no messaging.
 *  - pe-a-rm/pe-b-rm/pe-c-rm — Provider Exchange awareness experiment (RM-only audience)
 *  - fixed-rm-rra            — RM + RRA fixed variation (not part of an experiment)
 *  - fixed-jan-survey        — January survey fixed variation (not part of an experiment)
 */
export type RmPlgVariation = 'pe-a-rm' | 'pe-b-rm' | 'pe-c-rm' | 'fixed-rm-rra' | 'fixed-jan-survey';

export interface ProviderExchangeCopy {
    /** Short label rendered at the top of the card / as the popup modal title. */
    title: string;
    /** Headline copy driven by the LD variation (shared by the sidebar card and popup modal). */
    headline: string;
    /** Body copy shown in the popup modal. */
    body: string;
    /** Label for the primary "Get Early Access" / Join Waitlist CTA. */
    joinWaitlistLabel: string;
    /** Label for the secondary ROI impact calculator link. */
    roiCalculatorLabel: string;
    /**
     * When set, the sidebar card's primary CTA becomes a plain link-out (label + href) instead of
     * the waitlist join flow — no Slack lead alert, no localStorage join state, no confirmation
     * view. The popup modal is unaffected and keeps its own waitlist CTA regardless of this field.
     */
    sidenavCta?: {
        label: string;
        href: string;
    };
}

const PROVIDER_EXCHANGE_TITLE = 'Provider Exchange';
const JOIN_WAITLIST_LABEL = "I'm interested";
const ROI_CALCULATOR_LABEL = "Estimate my team's potential impact";

/* eslint-disable @typescript-eslint/naming-convention -- keys are the exact `rm-plg` LD variation values */
export const PROVIDER_EXCHANGE_COPY: Record<RmPlgVariation, ProviderExchangeCopy> = {
    'pe-a-rm': {
        title: PROVIDER_EXCHANGE_TITLE,
        headline: 'Still faxing record requests and making follow-up phone calls?',
        body:
            'Send, track, and manage medical record requests in one place, reducing the need for ' +
            'faxing, phone calls, and manual follow-up.',
        joinWaitlistLabel: JOIN_WAITLIST_LABEL,
        roiCalculatorLabel: ROI_CALCULATOR_LABEL,
    },
    'pe-b-rm': {
        title: PROVIDER_EXCHANGE_TITLE,
        headline: 'Still waiting on patient records?',
        body:
            'Waiting on outside records can delay referrals and patient care. Keep medical record ' +
            'requests organized and track their status from one place.',
        joinWaitlistLabel: JOIN_WAITLIST_LABEL,
        roiCalculatorLabel: ROI_CALCULATOR_LABEL,
    },
    'pe-c-rm': {
        title: PROVIDER_EXCHANGE_TITLE,
        headline: 'Still chasing records by fax or phone?',
        body: 'Request and track records from other providers in one place with Provider Exchange.',
        joinWaitlistLabel: JOIN_WAITLIST_LABEL,
        roiCalculatorLabel: ROI_CALCULATOR_LABEL,
        sidenavCta: {
            label: 'See the difference',
            href: 'https://datavantnewstg.wpenginepowered.com/solutions/provider-exchange',
        },
    },
    'fixed-rm-rra': {
        title: PROVIDER_EXCHANGE_TITLE,
        headline: 'Already using Auto-Fulfill? Manage medical record requests with Datavant, too.',
        body:
            "You're already using Auto-Fulfill for payer requests. Now manage medical record " +
            'requests in one place with Datavant.',
        joinWaitlistLabel: JOIN_WAITLIST_LABEL,
        roiCalculatorLabel: ROI_CALCULATOR_LABEL,
    },
    'fixed-jan-survey': {
        title: PROVIDER_EXCHANGE_TITLE,
        headline: 'You asked, we listened.',
        body:
            'Earlier this year, you told us you were interested in improving how your organization ' +
            "manages medical record requests from other providers. We'd love to show you what's " +
            'coming next.',
        joinWaitlistLabel: JOIN_WAITLIST_LABEL,
        roiCalculatorLabel: ROI_CALCULATOR_LABEL,
    },
};
/* eslint-enable @typescript-eslint/naming-convention */

/**
 * Reads the `rm-plg` flag and returns the active variation, or `false` when the flag is off or
 * set to a value we don't recognize (fail closed — show no messaging).
 */
export const useRmPlgVariation = (): RmPlgVariation | false => {
    const flags = useFlags();
    const value = flags[RM_PLG_FLAG];
    const variation =
        typeof value === 'string' && value in PROVIDER_EXCHANGE_COPY ? (value as RmPlgVariation) : false;

    useEffect(() => {
        if (!variation) return;
        // @ts-expect-error - window['pendo'] is untyped
        if (typeof window !== 'undefined' && typeof window['pendo'] !== 'undefined') {
            // @ts-expect-error - window['pendo'] is untyped
            window['pendo'].updateOptions({ visitor: { provider_exchange_variant: variation } });
        }
    }, [variation]);

    return variation;
};

/** Returns the copy for a given variation. */
export const getProviderExchangeCopy = (variation: RmPlgVariation): ProviderExchangeCopy =>
    PROVIDER_EXCHANGE_COPY[variation];

/** Element `id` attributes for the sidebar card CTAs, suffixed with the LD variation for per-variant Pendo tracking. */
export const getRmPlgNavIds = (variation: RmPlgVariation) =>
    ({
        joinWaitlist: `rm-plg-sidenav-cta-button-${variation}`,
        roiCalculator: `rm-plg-sidenav-secondary-cta-button-${variation}`,
    } as const);

/** Element `id` attributes for the popup modal, suffixed with the LD variation for per-variant Pendo tracking. */
export const getRmPlgPopupIds = (variation: RmPlgVariation) =>
    ({
        modal: `rm-plg-popup-modal-${variation}`,
        joinWaitlist: `rm-plg-popup-modal-cta-button-${variation}`,
        roiCalculator: `rm-plg-popup-modal-secondary-cta-button-${variation}`,
        dismiss: `rm-plg-popup-modal-dismiss-button-${variation}`,
    } as const);

/** Destination for the ROI impact calculator link. */
export const PROVIDER_EXCHANGE_ROI_CALCULATOR_PATH = 'https://www.datavant.com/calculator/provider-exchange';

/** LaunchDarkly custom metric keys. Pass to `ldClient.track()`. */
export const RM_PLG_LD_METRIC_KEYS = {
    ctaClick: 'rm-plg-cta-click',
    ctaClickModal: 'rm-plg-cta-click-modal',
    ctaClickSidenav: 'rm-plg-cta-click-sidenav',
    roiCalculatorClick: 'rm-plg-roi-calculator-click',
    roiCalculatorClickModal: 'rm-plg-roi-calculator-click-modal',
    roiCalculatorClickSidenav: 'rm-plg-roi-calculator-click-sidenav',
    modalDismissed: 'rm-plg-modal-dismissed',
    modalImpression: 'rm-plg-modal-impression',
    sidenavImpression: 'rm-plg-sidenav-impression',
} as const;

/** localStorage key persisting that the user dismissed the popup — the sole lever for showing it. */
export const PEX_POPUP_DISMISSED_KEY = 'pex_popup_dismissed';

/**
 * localStorage key persisting that the user has joined the Provider Exchange waitlist. Set when the
 * "Get Early Access" CTA is clicked; other PLG surfaces should read this to avoid re-advertising to
 * a user who has already signed up.
 */
export const PEX_WAITLIST_JOINED_KEY = 'pex_waitlist_joined';

/**
 * localStorage key tracking how many more remounts the sidenav card should remain visible after
 * the user joins the waitlist. Set to PEX_POST_WAITLIST_REMOUNT_COUNT on join so the user can
 * still access the ROI calculator. Decremented on each mount; card hides when it reaches 0.
 */
export const PEX_POST_WAITLIST_REMOUNTS_KEY = 'pex_post_waitlist_remounts';

/** Number of additional sidenav card remounts to show after the user joins the waitlist. */
export const PEX_POST_WAITLIST_REMOUNT_COUNT = 3;

/**
 * In-app confirmation shown after the user joins the waitlist. Sourced from the legal-approved
 * Email Confirmation copy in the "PLG Messaging Legal Review" doc.
 */
export const PEX_POPUP_SUCCESS_COPY = {
    title: "You're on the waitlist!",
    body: "We'll keep you updated as early access becomes available.",
} as const;

/**
 * Builds the Slack lead alert body for either PLG surface.
 * Fetchers are passed as callbacks so this stays decoupled from tRPC/React.
 */
export const buildRmPlgLeadMessage = async ({
    action,
    surface,
    variation,
    copyHeadline,
    tenantUUID,
    popupPaused = false,
    fetchVolume,
    fetchSites,
}: {
    action: 'waitlist' | 'calculator';
    surface: 'side nav' | 'modal';
    variation: RmPlgVariation;
    copyHeadline: string;
    tenantUUID: string | undefined;
    /** True when the popup was paused, so the user only saw the side nav card. */
    popupPaused?: boolean;
    fetchVolume: () => Promise<number | null>;
    fetchSites: () => Promise<{ startEndBlock: Array<{ state: string | null }> }>;
}): Promise<string> => {
    let annualPayerVolume: number | null = null;
    try {
        annualPayerVolume = await fetchVolume();
    } catch {
        // volume is best-effort
    }

    let state = '';
    try {
        const sites = await fetchSites();
        state = Array.from(new Set(sites.startEndBlock.map(row => row.state).filter(Boolean))).join(', ');
    } catch {
        // state is best-effort
    }

    const surfaceLabel = surface === 'side nav' ? ' (side nav)' : '';
    const headline =
        action === 'waitlist'
            ? `New Provider Exchange early access request${surfaceLabel}`
            : `New Provider Exchange ROI calculator click${surfaceLabel}`;

    return (
        `:bufo-blush: ${headline} :bufo-blush:\n\n` +
        `Variation: ${variation} — "${copyHeadline}"\n\n` +
        (popupPaused ? 'Popup: paused (side nav only)\n\n' : '') +
        `Tenant UUID: ${tenantUUID ?? 'unknown'}\n\n` +
        `State: ${state || 'unknown'}\n\n` +
        `Annual payer volume: ${annualPayerVolume ?? 'unknown'}`
    );
};

/**
 * Stable `id` attributes for the popup modal static elements (used by Pendo and tests).
 * CTA button IDs are dynamic (variation-suffixed) — use `getRmPlgPopupIds(variation)` for those.
 */
export const RM_PLG_POPUP_IDS = {
    modal: 'rm-plg-popup-modal',
    success: 'rm-plg-popup-success',
} as const;
